import type { Agg, Delta, Metric, MetricKind } from "@/lib/utils/daylight-insight/types";
import type { DashboardMetric } from "./types";

/** Best-effort unit from the SP's `MetricCalc` (there is no dedicated unit field
 *  yet). "Average"/"Count"/"Sum"/"AverageDIP" → a plain number; the rest map to
 *  their unit. NOTE: this means "avg days" metrics (ALOS) render unit-less until
 *  the SP sends an explicit unit. */
export function calcToKind(calc: string): MetricKind {
  const c = (calc || "").toLowerCase();
  if (c.includes("percent") || c === "pct") return "pct";
  if (c.includes("currency") || c.includes("money") || c.includes("dollar")) return "currency";
  if (c.includes("day")) return "days";
  if (c.includes("hour")) return "hours";
  if (c.includes("min")) return "minutes";
  return "count";
}

/** Roll-up semantics from `MetricCalc`: averages/percents aggregate by mean,
 *  counts/sums/currency by sum. Drives the By-Branch/Payor "Share" vs "vs Bench"
 *  column (a "Share" only makes sense for additive metrics). */
export function calcToAgg(calc: string): Agg {
  const c = (calc || "").toLowerCase();
  if (c.includes("percent") || c.includes("average") || c === "avg" || c === "pct") return "avg";
  return "sum";
}

/** Adapts an API metric into the seed-layer `Metric` shape the existing card and
 *  table helpers already understand (id/name/kind + direction). `benchmark` is
 *  null — the SP sends no numeric target, so cells/chips render uncolored rather
 *  than guess a good/bad state. */
export function apiToMetric(dm: DashboardMetric): Metric {
  return {
    id: dm.code,
    name: dm.name,
    kind: calcToKind(dm.calc),
    agg: calcToAgg(dm.calc),
    base: 0,
    benchmark: null,
    benchLabel: dm.benchmarkLabel ?? undefined,
    lowerBetter:
      dm.higherIsBetter === false ? true : dm.higherIsBetter === true ? false : undefined,
    formula: dm.formula || "",
    daily: [],
  };
}

/**
 * Period-over-period delta from the SP's current + prior values.
 *
 * The percentage divides by the MAGNITUDE of the prior period, not by the signed
 * value: on a metric that runs negative (Revenue Adjustments is a debit, so
 * every period is below zero) dividing by a negative base inverts the sign, and
 * a move from -88,155 to +7,580 — a rise — would have read as a fall. Using
 * |prev| keeps the sign of the percentage the sign of the actual change; whether
 * that change is GOOD is a separate question, answered by `higherIsBetter`.
 */
export function deltaOf(cur: number | null, prev: number | null): Delta {
  if (cur === null || prev === null) return { pct: 0, abs: 0 };
  const abs = cur - prev;
  if (prev === 0) return { pct: 0, abs };
  return { pct: (abs / Math.abs(prev)) * 100, abs };
}

/** Sparkline series for a metric: its trend points with the current value
 *  appended as the latest bar (the trend set carries only prior periods). */
export function sparkPoints(dm: DashboardMetric): number[] {
  const prior = dm.trend
    .map((t) => t.value)
    .filter((v): v is number => v !== null && v !== undefined);
  const pts = dm.currentValue !== null ? [...prior, dm.currentValue] : prior;
  return pts;
}
